/* Admin settings. Owner and office only.
 *
 * Reference data has to exist before the job form is usable, which is why this
 * screen matters early. Board stages live here too — the board reads this list,
 * and nothing in the app hardcodes a stage name, count or colour. */
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import {
  COMMON_TIMEZONES,
  ROLE_LABEL,
  allTimezones,
  formatInstant as formatInstantIn,
  isTimezone,
} from '@oneshot2pac/shared';
import { formatInstant } from '@/domain/time';
import { TONE_NAME } from '@/data/reference';
import { isOffice, useApp, type OrgDraft } from '@/store/useApp';
import { NO_AUTOFILL } from '@/ui/autofill';
import { Empty, PageHead } from '@/ui/controls';
import { Swatch } from '@/ui/Swatch';
import { useNarrowerThan, usePageGutter, withGutter } from '@/ui/viewport';
import type { Role, SettingsSection } from '@/types';

/* Below this the sections are drawn as a row above the content rather than a
 * rail beside it.
 *
 * The arithmetic: the rail costs 232px, and the lists beside it want their
 * width — the people table 720 of it, a stage row about 740 before its WIP
 * limit and flags fold under the name. Under about that sum plus the gutters
 * the sections move where they cost nothing but a row of height. */
const SETTINGS_RAIL = 1130;

const NAV: { label: string; key: SettingsSection }[] = [
  { label: 'Board stages', key: 'stages' },
  { label: 'Colours', key: 'colours' },
  { label: 'People', key: 'people' },
  { label: 'Organisation', key: 'org' },
];

/** The reorder arrows on a stage row. Square, and big enough for a glove. */
const nudge: CSSProperties = {
  width: 34,
  height: 34,
  minHeight: 34,
  padding: 0,
  display: 'inline-grid',
  placeItems: 'center',
  fontSize: 14,
  border: '1px solid var(--line)',
  borderRadius: 9,
};

/* The width each of these lists stops being readable at — the sum of what its
 * columns need, not a device. Below it the list scrolls sideways inside its own
 * panel, the way the jobs and customers tables do; the page never does.
 *
 * The stage list's is the row with its WIP limit and flags folded under the
 * name: the index, the nudges, the colour and Edit (338 with their gaps) and a
 * name that still reads beside them. It used to be 900 — the whole row on one
 * line — which put the Edit button past the edge of every iPad. */
const WIDE = { stages: 520, colours: 640, people: 720 };

/** What a stage's WIP limit and flags take on a line: two fixed columns, so
 *  they line up down the list whenever they sit beside the name. */
const STAGE_META = '96px 118px';

/** A dense list, scrolling sideways in its own container rather than the app's. */
function Wide({ min, children }: { min: number; children: ReactNode }) {
  return (
    <div style={{ overflowX: 'auto', overscrollBehavior: 'contain', maxWidth: '100%' }}>
      <div style={{ minWidth: min }}>{children}</div>
    </div>
  );
}

const moneyTag = (yes: boolean): CSSProperties => ({
  fontFamily: 'var(--font-heading)',
  fontSize: 9.5,
  fontWeight: 600,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  padding: '3px 8px',
  borderRadius: 999,
  background: yes ? 'var(--accent-tint)' : 'var(--line-soft)',
  color: yes ? 'var(--accent-deep)' : 'var(--ink-muted)',
});

export function SettingsScreen() {
  const gutter = usePageGutter();
  const st = useApp();
  const railHasRoom = !useNarrowerThan(SETTINGS_RAIL);

  return (
    /* The page gutter and the foot — the band above takes its sides from this.
       Deeper at the foot than the framed screens: this one scrolls, and a list
       ending hard against the bottom of the glass reads as cut off. */
    <section style={{ flex: 1, padding: withGutter(gutter, 0, 48) }}>
      {/* Nothing here changes what is on screen — the sections do that, and they
          are a rail or a row below — so the band is the title and the one line
          saying whose screen this is, beside it rather than under it. */}
      <PageHead
        title="Settings"
        caption="Owner and office only. Reference data feeds every job form."
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: railHasRoom ? '212px minmax(0,1fr)' : 'minmax(0,1fr)',
          gap: railHasRoom ? 20 : 14,
          alignItems: 'start',
        }}
      >
        {/* The sections are a rail beside the content while there is width for
            both, and a row above it when there is not — the content is what
            needs the pixels, and a pill row reads the same either way. */}
        <nav
          aria-label="Settings sections"
          style={{
            display: 'flex',
            flexDirection: railHasRoom ? 'column' : 'row',
            flexWrap: railHasRoom ? 'nowrap' : 'wrap',
            gap: railHasRoom ? 2 : 6,
          }}
        >
          {NAV.map((n) => {
            const on = st.settings === n.key;
            return (
              <button
                key={n.key}
                type="button"
                aria-current={on ? 'true' : undefined}
                onClick={() => st.leave(() => st.set('settings', n.key))}
                style={{
                  textAlign: 'left',
                  minHeight: 48,
                  padding: '0 14px',
                  flex: 'none',
                  borderRadius: 12,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: on ? 'var(--on-accent)' : 'transparent',
                  border: `1px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
                  color: 'var(--ink)',
                }}
              >
                {n.label}
              </button>
            );
          })}
        </nav>

        <div>
          {st.settings === 'colours' ? <Colours /> : null}
          {st.settings === 'stages' ? <Stages /> : null}
          {st.settings === 'people' ? <People /> : null}
          {st.settings === 'org' ? <Organisation /> : null}
        </div>
      </div>
    </section>
  );
}

/** The shop's colour list — the biggest piece of reference data, and the one
 *  every job card draws its chip from.
 *
 *  The server's list, in the order it answers: what is on offer, most used
 *  first, and the archived ones after them. Archived colours stay on screen
 *  because jobs are still sprayed in them, and Bring back is how one returns.
 *
 *  The search is over the list already here rather than a question for the
 *  server: it is a few dozen colours, not an archive, and it comes with the
 *  session. Local state, like the schedule's ⓘ — nothing typed in it is work to
 *  lose. */
function Colours() {
  const st = useApp();
  const office = isOffice(st);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? st.colours.filter((c) =>
        [c.brand, c.code, c.name, `${c.brand} ${c.name}`].some((v) =>
          v.toLowerCase().includes(needle),
        ),
      )
    : st.colours;

  return (
    <div className="blueprint" style={{ background: 'var(--surface)', padding: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding: '16px 16px 14px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2
            style={{
              fontSize: 16,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              margin: '0 0 2px',
            }}
          >
            Colours
          </h2>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-muted)' }}>
            Swatches are approximate — for reading the board, never for matching.
          </p>
        </div>
        <input
          className="input"
          type="search"
          name="colourSearch"
          value={q}
          placeholder="Search brand, code, name"
          aria-label="Search colours"
          autoComplete={NO_AUTOFILL}
          onChange={(e) => setQ(e.target.value)}
          style={{ marginLeft: 'auto', width: 230, minWidth: 0, height: 40, fontSize: 16 }}
        />
        {office ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={st.openAddColour}
            style={{ height: 40 }}
          >
            Add colour
          </button>
        ) : null}
      </div>
      {st.colours.length === 0 ? (
        <div style={{ padding: '0 16px 16px' }}>
          <Empty>
            {office
              ? 'No colours on the list yet. Add the first one — every job is sprayed in one.'
              : 'No colours on the list yet. The owner and the office add them.'}
          </Empty>
        </div>
      ) : shown.length === 0 ? (
        <div style={{ padding: '0 16px 16px' }}>
          <Empty>
            No colour matches &ldquo;{q.trim()}&rdquo;. Try the code off the fan deck, or clear the
            search{office ? ' and add it' : ''}.
          </Empty>
        </div>
      ) : (
        <Wide min={WIDE.colours}>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 16, width: 60 }}>Chip</th>
                <th>Brand</th>
                <th>Code</th>
                <th>Name</th>
                <th style={{ textAlign: 'right' }}>Jobs</th>
                {office ? <th style={{ paddingRight: 16, textAlign: 'right' }}>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => {
                // Archived rows read as set aside rather than broken: the chip
                // and the code stay, the text steps back, and the row says
                // Archived in words — never by colour alone.
                const muted = c.archived ? 'var(--ink-muted)' : undefined;
                return (
                  <tr key={c.id}>
                    <td style={{ paddingLeft: 16, opacity: c.archived ? 0.6 : 1 }}>
                      <Swatch code={c.code} size={34} />
                    </td>
                    <td style={{ fontSize: 13.5, color: muted }}>{c.brand}</td>
                    <td
                      style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: 13,
                        fontWeight: 500,
                        color: muted,
                      }}
                    >
                      {c.code}
                    </td>
                    <td style={{ fontSize: 13.5, color: muted }}>
                      {c.name}
                      {c.archived ? (
                        <span
                          className="label-xs"
                          style={{ display: 'block', fontSize: 9.5, letterSpacing: '0.09em' }}
                        >
                          Archived — not offered on new work
                        </span>
                      ) : null}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontFamily: 'var(--font-data)',
                        fontSize: 13,
                        color: 'var(--ink-muted)',
                      }}
                    >
                      {c.jobs}
                    </td>
                    {office ? (
                      <td style={{ paddingRight: 16, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {c.archived ? (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={st.savingColour}
                            aria-label={`Bring back ${c.brand} ${c.name}`}
                            onClick={() => void st.restoreColour(c)}
                          >
                            Bring back
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              aria-label={`Edit ${c.brand} ${c.name}`}
                              onClick={() => st.openEditColour(c)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              disabled={st.savingColour}
                              aria-label={`Archive ${c.brand} ${c.name}`}
                              onClick={() => void st.archiveColour(c)}
                              style={{ color: 'var(--ink-muted)' }}
                            >
                              Archive
                            </button>
                          </>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Wide>
      )}
      {!office ? (
        <p style={{ margin: 0, padding: '0 16px 14px', fontSize: 12.5, color: 'var(--ink-muted)' }}>
          Ask the office if a colour is missing, or one of these is wrong.
        </p>
      ) : null}
    </div>
  );
}

function Stages() {
  const st = useApp();
  const office = isOffice(st);
  const held = (id: string) => st.jobs.filter((j) => j.stage === id).length;

  return (
    <div className="blueprint" style={{ background: 'var(--surface)', padding: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2
            style={{
              fontSize: 16,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              margin: '0 0 2px',
            }}
          >
            Board stages
          </h2>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-muted)' }}>
            {office
              ? 'Drag a stage to reorder it, or use the arrows. Each stage’s colour tints its board column. The board reads this list — nothing is hardcoded.'
              : 'The board reads this list — nothing is hardcoded. Only the owner and the office change it.'}
          </p>
        </div>
        {office ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={st.openAddStage}
            style={{ height: 40, marginLeft: 'auto' }}
          >
            Add stage
          </button>
        ) : null}
      </div>
      <Wide min={WIDE.stages}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {st.stages.map((s, i) => {
            const tone = s.tone;
            const jobs = held(s.id);
            return (
              <div
                key={s.id}
                draggable={office}
                onDragStart={() => st.beginStageDrag(s.id)}
                onDragEnd={st.endStageDrag}
                onDragOver={(e) => {
                  if (office) e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  st.dropOnStage(s.id);
                }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '26px 76px 128px minmax(0,1fr) auto',
                  gap: 12,
                  alignItems: 'center',
                  padding: '11px 2px',
                  borderTop: '1px solid var(--line-soft)',
                  cursor: office ? 'grab' : undefined,
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: 12,
                    color: 'var(--ink-muted)',
                  }}
                >
                  {i + 1}
                </span>

                {/* Drag is a pointer-device enhancement; these are how the order
                  actually changes on a keyboard, and on a touch screen. */}
                <span style={{ display: 'flex', gap: 4 }}>
                  {office ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        aria-label={`Move ${s.name} up`}
                        disabled={i === 0}
                        onClick={() => st.moveStage(s.id, -1)}
                        style={{ ...nudge, opacity: i === 0 ? 0.3 : 1 }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        aria-label={`Move ${s.name} down`}
                        disabled={i === st.stages.length - 1}
                        onClick={() => st.moveStage(s.id, 1)}
                        style={{ ...nudge, opacity: i === st.stages.length - 1 ? 0.3 : 1 }}
                      >
                        ↓
                      </button>
                    </>
                  ) : null}
                </span>

                <button
                  type="button"
                  title={`Change ${s.name}'s board colour`}
                  onClick={() => st.openPicker('stageTone', null, s.id)}
                  disabled={!office}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minHeight: 36,
                    padding: '0 10px',
                    borderRadius: 10,
                    border: '1px solid var(--line)',
                    background: 'var(--surface)',
                    cursor: office ? 'pointer' : 'default',
                    textAlign: 'left',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      width: 16,
                      height: 16,
                      flex: 'none',
                      borderRadius: 5,
                      background: tone || 'transparent',
                      border: tone ? '1px solid var(--chip-border)' : '1px dashed var(--line)',
                    }}
                  />
                  <span style={{ fontSize: 11.5, color: 'var(--ink-muted)' }}>
                    {TONE_NAME[tone] ?? 'Custom'}
                  </span>
                </button>

                {/* The name, then the limit and the flags, as one wrapping row
                    rather than three grid columns. Where there is room the two
                    sit on one line, the limit and flags in fixed columns at the
                    right so they line up down the list; where there is not —
                    an iPad in portrait, or an iPad with the rail beside it —
                    they drop under the name, and Edit stays on the screen.
                    There is no breakpoint: the name's flex basis is what
                    decides, so a long stage name folds its own row and nobody
                    else's. */}
                <span
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    columnGap: 12,
                    rowGap: 4,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 8,
                      flex: '1 1 160px',
                      minWidth: 0,
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600, overflowWrap: 'anywhere' }}>
                      {s.name}
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: 11.5,
                        color: 'var(--ink-muted)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {jobs ? `${jobs} ${jobs === 1 ? 'job' : 'jobs'}` : 'Empty'}
                    </span>
                  </span>

                  <span
                    style={{
                      display: 'grid',
                      gridTemplateColumns: STAGE_META,
                      columnGap: 12,
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: 12,
                        color: s.wip > 0 && jobs > s.wip ? 'var(--urgent)' : 'var(--ink-muted)',
                      }}
                    >
                      {s.wip ? `WIP ${s.wip}` : 'No limit'}
                    </span>

                    <span className="label-xs" style={{ fontSize: 9.5, letterSpacing: '0.09em' }}>
                      {s.terminal ? 'Terminal' : s.def ? 'Default for new jobs' : ''}
                    </span>
                  </span>
                </span>

                <span style={{ textAlign: 'right' }}>
                  {office ? (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => st.openEditStage(s)}
                    >
                      Edit
                    </button>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </Wide>
      {!office ? (
        <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--ink-muted)' }}>
          Ask the office if the board needs a new step, or one of these renaming.
        </p>
      ) : null}
    </div>
  );
}

/** The shop's people list — the one settings section that is already real.
 *
 *  The office adds people here, and adding one creates the account: there is no
 *  public signup, and nobody gets in on their own (IDEA.md §9). Invitation email
 *  replaces the handed-over password later; the endpoints for it already exist.
 *
 *  Editing changes what someone may do — role, money access, whether they are
 *  still in the shop. It never touches their password: that is theirs. */
function People() {
  const st = useApp();
  const office = isOffice(st);
  const loadPeople = st.loadPeople;

  // Only an owner may change an owner, in either direction. The real membership
  // decides, not the account menu's role preview.
  const owner = st.session?.membership.role === 'owner';
  const editable = (role: Role) => owner || role !== 'owner';

  useEffect(() => {
    // The role switch in the account menu is a preview: as a previewed staff
    // member the office would get a 403, so don't ask.
    if (office) void loadPeople();
  }, [office, loadPeople]);

  return (
    <div className="blueprint" style={{ background: 'var(--surface)', padding: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2
            style={{
              fontSize: 16,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              margin: '0 0 2px',
            }}
          >
            People
          </h2>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-muted)' }}>
            The office adds people here, and that creates their account. There is no public signup.
          </p>
        </div>
        {office ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={st.openAddPerson}
            style={{ height: 40, marginLeft: 'auto' }}
          >
            Add person
          </button>
        ) : null}
      </div>

      {!office ? (
        <Empty>
          Only the owner and the office can see who is on the shop&rsquo;s list. Ask them if someone
          needs adding.
        </Empty>
      ) : st.peopleStatus === 'error' ? (
        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}
        >
          <p
            role="alert"
            style={{
              margin: 0,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'var(--urgent-tint)',
              border: '1px solid var(--urgent)',
              fontSize: 13.5,
              lineHeight: 1.45,
            }}
          >
            {st.peopleError}
          </p>
          <button type="button" className="btn btn-secondary" onClick={() => void st.loadPeople()}>
            Try again
          </button>
        </div>
      ) : st.people.length === 0 ? (
        <Empty>
          {st.peopleStatus === 'ready'
            ? 'Nobody here yet. Add the first person and hand them their password.'
            : 'Reading the people list…'}
        </Empty>
      ) : (
        <Wide min={WIDE.people}>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Money access</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Signed in</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {st.people.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontSize: 13.5 }}>
                    <span style={{ fontWeight: 600 }}>{p.user.name}</span>
                    {p.user.id === st.session?.user.id ? (
                      <span style={{ color: 'var(--ink-muted)' }}> — you</span>
                    ) : null}
                    <span
                      style={{
                        display: 'block',
                        fontFamily: 'var(--font-data)',
                        fontSize: 12,
                        color: 'var(--ink-muted)',
                      }}
                    >
                      {p.user.email}
                    </span>
                  </td>
                  <td style={{ fontSize: 13.5 }}>{ROLE_LABEL[p.role]}</td>
                  <td>
                    <span style={moneyTag(p.financialAccess)}>
                      {p.financialAccess ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--ink-muted)' }}>
                    {p.status === 'active' ? 'Active' : 'Deactivated'}
                  </td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontFamily: 'var(--font-data)',
                      fontSize: 12.5,
                      color: 'var(--ink-muted)',
                    }}
                  >
                    {p.lastLoginAt ? formatInstant(p.lastLoginAt) : 'Not yet'}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {editable(p.role) ? (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => st.openEditPerson(p)}
                      >
                        Edit
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Wide>
      )}

      {office && !owner && st.people.some((p) => p.role === 'owner') ? (
        <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--ink-muted)' }}>
          Only the owner can change an owner&rsquo;s account.
        </p>
      ) : null}
    </div>
  );
}

/* The four things the shop is: what it is called, its ABN, the timezone its
   dates are read in, and the prefix the next job code is made from. Real
   columns on the organisation — the form is filled from the session on the way
   in and written back through /api/organisation. */
const ORG_FIELDS: {
  key: keyof OrgDraft;
  label: string;
  hint: string;
  mono?: boolean;
  /** A field picked from a list rather than typed. */
  pick?: boolean;
}[] = [
  { key: 'name', label: 'Organisation name', hint: 'What the shop is called on the paperwork.' },
  { key: 'abn', label: 'ABN', hint: 'Eleven digits. Leave it blank until you have it.' },
  {
    key: 'timezone',
    label: 'Timezone',
    hint: 'What the shop means by “today”. Every date in the app is read in it, and every time.',
    pick: true,
    mono: true,
  },
  {
    key: 'jobCodePrefix',
    label: 'Job code prefix',
    hint: 'What the next job code starts with — “J-” gives J-1042. Jobs already in the shop keep their codes.',
    mono: true,
  },
];

/** Every zone the browser knows, the ones this shop would pick first.
 *
 *  Built once at module scope rather than per render: it is four hundred
 *  strings and it does not change while the app is open. */
const ZONES = allTimezones();

const COMMON = new Set<string>(COMMON_TIMEZONES);

/** What the clock says in a zone right now.
 *
 *  The point of showing it is that a timezone is otherwise unverifiable — a
 *  list of four hundred IANA names all look equally plausible, and the office
 *  finds out they picked the wrong one when a job reads as due yesterday. The
 *  time is the check: if it does not say what the clock on the wall says, it is
 *  the wrong zone. */
function nowIn(zone: string): string {
  if (!isTimezone(zone)) return '';
  return formatInstantIn(new Date().toISOString(), zone);
}

function Organisation() {
  const st = useApp();
  const office = isOffice(st);
  const changed = ORG_FIELDS.some((f) => st.org[f.key] !== st.savedOrg[f.key]);

  return (
    <div className="blueprint" style={{ background: 'var(--surface)', padding: 16, maxWidth: 520 }}>
      <h2
        style={{
          fontSize: 16,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          margin: '0 0 14px',
        }}
      >
        Organisation
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {st.orgError ? (
          <p
            role="alert"
            style={{
              margin: 0,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'var(--urgent-tint)',
              border: '1px solid var(--urgent)',
              color: 'var(--ink)',
              fontSize: 13.5,
              lineHeight: 1.45,
            }}
          >
            {st.orgError}
          </p>
        ) : null}

        {/* Read-only for anyone but the office, and it says so rather than
            drawing four dead inputs (IDEA.md §11). */}
        {office ? null : (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-muted)' }}>
            Read-only — the owner and the office change the shop’s details.
          </p>
        )}

        {ORG_FIELDS.map((f) => (
          <label
            key={f.key}
            className="field"
            style={{ display: 'flex', flexDirection: 'column', gap: 5 }}
          >
            <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
              {f.label}
            </span>
            {office && f.pick ? (
              /* A native select, not the app's picker dialog: it is four
                 hundred options, and the platform's own control already
                 searches them by typing on a desktop and gives an iPad a wheel.
                 The zones the shop would actually pick are grouped at the top
                 so the common case is the first thing under the cursor. */
              <select
                className="input"
                name={f.key}
                value={st.org[f.key]}
                autoComplete={NO_AUTOFILL}
                onChange={(e) => st.setOrg({ [f.key]: e.target.value })}
                style={{ height: 48, fontSize: 16, fontFamily: 'var(--font-data)' }}
              >
                {/* A zone the shop already has that this browser does not know
                    still has to be in the list, or opening the form would
                    silently change it to whatever sits at the top. */}
                {ZONES.includes(st.org[f.key]) ? null : (
                  <option value={st.org[f.key]}>{st.org[f.key] || 'Not set'}</option>
                )}
                <optgroup label="Australia and New Zealand">
                  {ZONES.filter((z) => COMMON.has(z)).map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Everywhere else">
                  {ZONES.filter((z) => !COMMON.has(z)).map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </optgroup>
              </select>
            ) : office ? (
              <input
                className="input"
                name={f.key}
                value={st.org[f.key]}
                autoComplete={NO_AUTOFILL}
                spellCheck={false}
                onChange={(e) => st.setOrg({ [f.key]: e.target.value })}
                style={{
                  height: 48,
                  fontSize: 16,
                  fontFamily: f.mono ? 'var(--mono)' : undefined,
                }}
              />
            ) : (
              <span style={{ fontSize: 16, fontFamily: f.mono ? 'var(--mono)' : undefined }}>
                {st.org[f.key] || '—'}
              </span>
            )}
            <span style={{ fontSize: 12, color: 'var(--ink-muted)', lineHeight: 1.45 }}>
              {f.hint}
              {f.pick && nowIn(st.org[f.key]) ? (
                <>
                  {' '}
                  It is{' '}
                  <strong style={{ color: 'var(--ink)', fontWeight: 500 }}>
                    {nowIn(st.org[f.key])}
                  </strong>{' '}
                  there now.
                </>
              ) : null}
            </span>
          </label>
        ))}

        {office ? (
          <button
            type="button"
            className="btn btn-primary blueprint"
            onClick={() => void st.saveOrg()}
            disabled={st.orgSaving || !changed}
            style={{ height: 48, alignSelf: 'flex-start', paddingInline: 18 }}
          >
            {st.orgSaving ? 'Saving…' : 'Save changes'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
